import { useParams, useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { openCheckout } from '../services/nativeBrowser';
import { useEffect, useState, useContext } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { UserContext } from '../contexts/UserContext';
import { MembershipContext } from '../contexts/MembershipContext';
import { useContact } from '../contexts/ContactContext';

const PurchaseConfirm = () => {
  const { offerId } = useParams();
  const navigate = useNavigate();
  const { firestoreUser, loading: userDataLoading } = useContext(UserContext);
  const { getCashFeePercentage } = useContext(MembershipContext);
  const { openContact } = useContact();
  
  const [offerData, setOfferData] = useState(null);
  const [targetPedal, setTargetPedal] = useState(null);
  const [buyerUserData, setBuyerUserData] = useState({ username: '', badge: '', membershipTier: '', photoURL: '', averageRating: 0 });
  const [sellerUserData, setSellerUserData] = useState({ username: '', badge: '', membershipTier: '', photoURL: '', averageRating: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processingPayment, setProcessingPayment] = useState(false);
  const [salesTax, setSalesTax] = useState(null);
  const [calculatingSalesTax, setCalculatingSalesTax] = useState(false);

  const getBadge = (count) => {
    if (count === undefined || count === null || isNaN(count)) return '0';
    return count.toString();
  };

  const getMembershipBadge = (tier) => {
    if (!tier) return null;
    
    const badgeConfig = {
      '9V': { text: '9V', color: '#FFD700', textColor: '#000' },
      '18V': { text: '18V', color: '#C0C0C0', textColor: '#000' },
      'free': { text: 'FREE', color: '#E5E7EB', textColor: '#374151' }
    };
    
    const config = badgeConfig[tier.toLowerCase()];
    if (!config) return null;
    
    return (
      <div
        style={{
          backgroundColor: config.color,
          color: config.textColor,
          fontWeight: 'bold',
          borderRadius: '9999px',
          padding: '2px 8px',
          fontSize: '0.75rem',
          border: '1px solid #d1d5db'
        }}
      >
        {config.text}
      </div>
    );
  };

  const getMembershipProfileBadge = (tier) => {
    if (!tier) return null;
    
    const badgeConfig = {
      '9V': { text: '9V', color: '#FFD700', textColor: '#000' },
      '18V': { text: '18V', color: '#C0C0C0', textColor: '#000' },
      'free': { text: 'FREE', color: '#E5E7EB', textColor: '#374151' }
    };
    
    const config = badgeConfig[tier.toLowerCase()];
    if (!config) return null;
    
    return (
      <div
        style={{
          position: 'absolute',
          top: '-2px',
          left: '-2px',
          backgroundColor: config.color,
          color: config.textColor,
          fontWeight: 'bold',
          borderRadius: '50%',
          width: '16px',
          height: '16px',
          fontSize: '0.65rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid #fff',
          boxShadow: '0 0 2px rgba(0,0,0,0.3)',
        }}
      >
        {config.text}
      </div>
    );
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        // Load purchase offer data
        const offerDoc = await getDoc(doc(db, 'purchaseOffers', offerId));
        if (!offerDoc.exists()) {
          setError('Purchase offer not found.');
          setLoading(false);
          return;
        }

        const offer = offerDoc.data();
        setOfferData({ id: offerDoc.id, ...offer });

        // Load target pedal data
        const pedalDoc = await getDoc(doc(db, 'listings', offer.targetPedalId));
        if (!pedalDoc.exists()) {
          setError('Target pedal not found.');
          setLoading(false);
          return;
        }
        setTargetPedal({ id: pedalDoc.id, ...pedalDoc.data() });

        // Load buyer and seller user data
        const [buyerDoc, sellerDoc] = await Promise.all([
          getDoc(doc(db, 'publicProfiles', offer.initiatorId)),
          getDoc(doc(db, 'publicProfiles', offer.responderId))
        ]);

        if (buyerDoc.exists()) {
          const buyerData = buyerDoc.data();
          setBuyerUserData({
            username: buyerData.username || buyerData.displayName || 'Unknown',
            badge: getBadge(buyerData.completedTrades),
            membershipTier: buyerData.membershipTier,
            photoURL: buyerData.photoURL || '/images/default-avatar.png',
            averageRating: buyerData.averageRating || 0
          });
        }

        if (sellerDoc.exists()) {
          const sellerData = sellerDoc.data();
          setSellerUserData({
            username: sellerData.username || sellerData.displayName || 'Unknown',
            badge: getBadge(sellerData.completedTrades),
            membershipTier: sellerData.membershipTier,
            photoURL: sellerData.photoURL || '/images/default-avatar.png',
            averageRating: sellerData.averageRating || 0
          });
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading purchase confirmation data:', err);
        setError('Failed to load purchase data.');
        setLoading(false);
      }
    };

    // Wait for auth to resolve before deciding login-vs-load. On a fresh load or
    // pull-to-refresh reload, auth.currentUser is briefly null; driving off
    // onAuthStateChanged means a logged-in user sees the loading state (and then
    // the page) rather than a flash of "You must be logged in".
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setError('You must be logged in to view this page.');
        setLoading(false);
        return;
      }
      loadData();
    });

    return () => unsubscribe();
  }, [offerId]);

  // Calculate sales tax when offer data is loaded
  useEffect(() => {
    const calculateTax = async () => {
      if (offerData && offerData.shippingAddress && !userDataLoading && firestoreUser) {
        const agreedAmount = offerData.acceptedAmount || offerData.offerAmount;
        const cashFee = Math.round(agreedAmount * (getCashFeePercentage() / 100) * 100) / 100;
        
        await calculateSalesTaxAmount(offerData.shippingAddress, agreedAmount, cashFee);
      }
    };
    
    calculateTax();
  }, [offerData, userDataLoading, firestoreUser]);

  const calculateSalesTaxAmount = async (shippingAddress, purchaseAmount, platformFee) => {
    try {
      setCalculatingSalesTax(true);
      console.log('💰 Calculating sales tax...');
      
      const calculateSalesTax = httpsCallable(functions, 'calculateSalesTax');
      const result = await calculateSalesTax({
        shippingAddress,
        purchaseAmount,
        platformFee
      });
      
      console.log('✅ Sales tax calculated:', result.data);
      setSalesTax(result.data);
      return result.data;
    } catch (error) {
      console.error('❌ Error calculating sales tax:', error);
      // If sales tax calculation fails, continue without it
      setSalesTax({ taxAmount: 0, enabled: false, error: error.message });
      return { taxAmount: 0, enabled: false };
    } finally {
      setCalculatingSalesTax(false);
    }
  };

  const handleProceedToPayment = async () => {
    setProcessingPayment(true);
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new Error('You must be logged in to complete the purchase.');
      }

      // Double-payment guard (client-side): the server purchase path has no
      // already-paid check (unlike trades), so a stale page could create a
      // second payable session. Re-read the offer and bail to progress if the
      // webhook already marked it paid.
      const freshOffer = await getDoc(doc(db, 'purchaseOffers', offerId));
      if (freshOffer.exists() && freshOffer.data().paymentStatus?.buyerPaid) {
        console.log('ℹ️ Purchase already paid — skipping checkout, going to progress');
        navigate(`/purchase-progress/${offerId}`);
        return;
      }

      console.log('🔐 Getting fresh auth token...');
      // Get fresh ID token to ensure authentication works
      const idToken = await user.getIdToken(true);
      console.log('✅ Auth token refreshed');

      const checkoutData = {
        tradeId: offerId, // Using offerId as tradeId for purchases
        type: 'purchase', // Indicate this is a purchase, not a trade
        userId: user.uid,
        returnTo: Capacitor.isNativePlatform() ? 'app' : undefined,
        cashAmount: offerData.acceptedAmount || offerData.offerAmount,
        salesTax: salesTax && salesTax.taxAmount > 0 ? {
          amount: salesTax.taxAmount,
          rate: salesTax.taxRate,
          jurisdiction: salesTax.taxJurisdiction,
          state: salesTax.state
        } : null,
        metadata: {
          type: 'purchase',
          offerId: offerId,
          targetPedalId: offerData.targetPedalId,
          buyerId: offerData.initiatorId,
          sellerId: offerData.responderId
        }
      };

      console.log('🛒 Creating Stripe checkout for purchase:', checkoutData);
      
      const baseUrl = 'https://us-central1-pedalxchange.cloudfunctions.net';
      
      const response = await fetch(`${baseUrl}/createStripeCheckout`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify(checkoutData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      console.log('📦 Stripe checkout result:', result);
      
      if (result.alreadyPaid) {
        // Server F2 guard: payment already recorded — go to progress instead
        // of erroring (or worse, opening a second checkout).
        console.log('ℹ️ Already paid — redirecting to purchase progress');
        navigate(`/purchase-progress/${offerId}`);
        return;
      }

      if (result.success && result.checkoutUrl) {
        console.log('✅ Checkout session created, redirecting to Stripe...');
        // Native only (web does a full-page redirect and Stripe's success_url
        // handles the return). `onReturn` fires on ANY sheet dismissal —
        // success AND cancel — and the webhook that writes buyerPaid can lag a
        // few seconds. So poll for buyerPaid: paid → progress; never confirmed
        // (cancelled, or webhook slower than the poll window) → stay here with
        // the button re-enabled so the user can retry. PurchaseProgress has no
        // live listener, so navigating there unpaid would show a stale
        // "Payment Pending" page with a path back into a second checkout.
        await openCheckout(result.checkoutUrl, {
          onReturn: () => {
            let attempts = 0;
            const maxAttempts = 6;
            const poll = async () => {
              attempts += 1;
              try {
                const snap = await getDoc(doc(db, 'purchaseOffers', offerId));
                if (snap.exists() && snap.data().paymentStatus?.buyerPaid) {
                  navigate(`/purchase-progress/${offerId}`);
                  return;
                }
              } catch (err) {
                console.warn('Post-checkout payment status check failed:', err);
              }
              if (attempts < maxAttempts) {
                setTimeout(poll, 2000);
              } else {
                console.log('ℹ️ Payment not confirmed after checkout sheet closed — staying on confirm');
                setProcessingPayment(false);
              }
            };
            poll();
          }
        });
      } else {
        throw new Error(result.error || 'Failed to create checkout session');
      }
    } catch (err) {
      console.error('❌ Error proceeding to payment:', err);
      setError(`Failed to proceed to payment: ${err.message}`);
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center">Loading purchase confirmation...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center text-red-600">{error}</div>
        <div className="text-center mt-4">
          <button 
            onClick={() => navigate(-1)}
            className="text-blue-600 hover:underline"
          >
            ← Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!offerData || !targetPedal) {
    return (
      <div className="p-4" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))' }}>
        <div className="text-center text-red-600">Purchase data not found.</div>
      </div>
    );
  }

  const userId = auth.currentUser?.uid;
  const isBuyer = userId === offerData.initiatorId;
  const agreedAmount = offerData.acceptedAmount || offerData.offerAmount;
  const cashFeePercentage = getCashFeePercentage();
  const cashProcessingFee = Math.round(agreedAmount * (cashFeePercentage / 100) * 100) / 100;
  const salesTaxAmount = salesTax && salesTax.taxAmount > 0 ? salesTax.taxAmount : 0;
  const totalAmount = agreedAmount + cashProcessingFee + salesTaxAmount;

  return (
    <div className="trade-confirm-outer" style={{ backgroundColor: '#F7F3EB', minHeight: 'calc(100vh - var(--navbar-height, 125px))', display: 'flex', flexDirection: 'column', padding: '1.5rem', boxSizing: 'border-box', overflowX: 'hidden' }}>
      <style jsx>{`
        .single-card-container {
          box-sizing: border-box;
        }
        @media (max-width: 768px) {
          .trade-confirm-outer {
            padding: 0.75rem !important;
          }
          .single-card-container {
            padding: 0.75rem !important;
            border-radius: 8px !important;
            margin: 0 !important;
            max-width: 100% !important;
          }
          .trade-confirm-outer h2 {
            font-size: 1.5rem !important;
            margin-bottom: 1rem !important;
            padding: 0 0.25rem !important;
          }
          .trade-info-text {
            font-size: 0.9375rem !important;
            padding: 0 !important;
          }
          .user-data-loading {
            margin: 0 0 1rem 0 !important;
            max-width: 100% !important;
          }
        }
      `}</style>

      <h2 className="text-2xl font-bold mb-4 text-center">Confirm Your Purchase</h2>

      {userDataLoading && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded user-data-loading" style={{ maxWidth: '900px', margin: '0 auto 1rem auto', width: '100%', boxSizing: 'border-box' }}>
          <p className="text-sm text-blue-800">
            ⏳ <strong>Loading account information...</strong> Please wait while we verify your identity and payment status.
          </p>
        </div>
      )}

      <div className="single-card-container" style={{
        maxWidth: '900px',
        margin: '0 auto',
        width: '100%',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '2rem',
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        boxSizing: 'border-box'
      }}>

        {/* 1. Purchase Information */}
        <div style={{ marginBottom: '2rem', paddingBottom: '2rem', borderBottom: '1px solid #e5e7eb' }}>
          <p className="text-gray-700 trade-info-text" style={{ fontSize: '1.125rem', lineHeight: '1.6', textAlign: 'center' }}>
            You are confirming the purchase of a <strong>{targetPedal.make} {targetPedal.model}</strong> from <strong>{sellerUserData.username}</strong> for <strong>${agreedAmount.toFixed(2)}</strong>.
          </p>
        </div>

        {/* 2. Payment Breakdown */}
        <div style={{ marginBottom: '2rem', background: '#fafafa', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '1.25rem', fontFamily: 'Nunito' }}>
          <h4 style={{ fontSize: '1.1rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>Payment breakdown</h4>

          <div style={{ fontSize: '0.7rem', fontWeight: '700', color: '#9ca3af', letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>Costs</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
            <span style={{ color: '#374151' }}>Purchase price</span>
            <span style={{ fontWeight: '600', color: '#374151' }}>${agreedAmount.toFixed(2)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
            <span style={{ color: '#374151' }}>Cash processing fee ({cashFeePercentage}%)</span>
            <span style={{ fontWeight: '600', color: '#374151' }}>${cashProcessingFee.toFixed(2)}</span>
          </div>

          {calculatingSalesTax && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
              <span style={{ color: '#6b7280' }}>Sales Tax</span>
              <span style={{ fontWeight: '600', color: '#6b7280' }}>Calculating...</span>
            </div>
          )}
          {!calculatingSalesTax && salesTaxAmount > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem', fontSize: '0.9rem' }}>
              <span style={{ color: '#374151' }}>
                {salesTax.state || 'CO'} Sales Tax ({salesTax.taxRatePercent}%)
              </span>
              <span style={{ fontWeight: '600', color: '#374151' }}>${salesTaxAmount.toFixed(2)}</span>
            </div>
          )}

          <div style={{ borderTop: '2px solid #d1d5db', marginTop: '0.75rem', paddingTop: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: '700', fontSize: '1rem', color: '#1f2937' }}>Total you'll pay</span>
            <span style={{ fontWeight: '700', fontSize: '1.1rem', color: '#b91c1c' }}>
              ${totalAmount.toFixed(2)}
            </span>
          </div>
        </div>

        {/* 3. Payment Button */}
        <div className="flex justify-center">
          <button
            onClick={handleProceedToPayment}
            disabled={processingPayment || userDataLoading || calculatingSalesTax}
            className="hover:opacity-80 transition-opacity"
            style={{
              backgroundColor: (processingPayment || userDataLoading || calculatingSalesTax) ? '#9CA3AF' : '#C9A227',
              color: (processingPayment || userDataLoading || calculatingSalesTax) ? '#E5E7EB' : 'white',
              border: 'none',
              borderRadius: '9999px',
              padding: '12px 24px',
              fontFamily: 'Nunito, sans-serif',
              fontWeight: 'bold',
              fontSize: '16px',
              cursor: (processingPayment || userDataLoading || calculatingSalesTax) ? 'not-allowed' : 'pointer',
              maxWidth: '300px',
              width: '100%'
            }}
          >
            {processingPayment ? 'Processing Payment...' :
             userDataLoading ? 'Loading account information...' :
             calculatingSalesTax ? 'Calculating sales tax...' :
             `Proceed to Payment ($${totalAmount.toFixed(2)})`}
          </button>
        </div>
      </div>

      {/* Fixed gap below content */}
      <div style={{ height: '60px' }}></div>

      {/* Footer */}
      <div className="py-4 trade-confirm-footer" style={{ paddingBottom: '10px', backgroundColor: '#F7F3EB' }}>
        <style jsx>{`
          @media (max-width: 768px) {
            .trade-confirm-footer {
              padding-bottom: 80px !important;
            }
          }
        `}</style>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Need help or found a bug?{' '}
              <button
                onClick={() => openContact({ category: 'support' })}
                className="hover:underline"
                style={{
                  color: '#5A9ECB',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit',
                  cursor: 'pointer'
                }}
              >
                Contact PXC Support
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseConfirm;